import { FeventStatus } from '@shared/api/fevent/fevent.interface';
import {
  fetcher,
  queryString,
  useRecoilQuery,
  useRecoilQueryInterVal,
} from '@shared/hooks/recoil-query';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';

import {
  activeFeventListForWorkerDataAtom,
  activeFeventListForWorkerStatusAtom,
  feventByShopDataAtomFamily,
  feventByShopStatusAtomFamily,
  feventCountByBugoBrandDataAtomFamily,
  feventCountByBugoBrandStatusAtomFamily,
  feventDataAtomFamily,
  feventDoingListDataAtom,
  feventDoingListStatusAtom,
  feventListAgencyAdminDataAtomFamily,
  feventListAgencyAdminStatusAtomFamily,
  feventListDataAtom,
  feventListStatusAtom,
  feventStatusAtomFamily,
} from './fevent.atom';

export const feventHookUrl = (feventId: string, bugoRole: string) =>
  `${bugoRole}/fevent/${feventId}/?${queryString({
    populate: [
      {
        path: 'user',
      },
      {
        path: 'funeralHomeInfo',
      },
      {
        path: 'roomInfo',
      },
      {
        path: 'deceasedInfo.dpImage',
      },
      {
        path: 'memberOrderList',
        populate: [
          {
            path: 'cashAccount',
          },
          {
            path: 'bugo',
          },
        ],
      },
      {
        path: 'cashAccounts',
      },
    ],
  })}`;

//* FeventIdRouter 부고수정 부고전송 답례글 전송
export const useFeventHook = function (id: string, disabled?: boolean) {
  const { bugoRole } = useAuth();
  useRecoilQuery(
    feventStatusAtomFamily(id),
    feventDataAtomFamily(id),
    feventHookUrl(id, bugoRole()),
    fetcher,
    disabled,
  );
};

export const feventListHookUrl = (page: number, step: number, bugoRole: LowerRole) => {
  return `${bugoRole}/fevent/?${queryString({
    options: {
      sort: { createdAt: -1 },
      limit: step,
      skip: step * page,
    },
    populate: [
      {
        path: 'roomInfo',
      },
      {
        path: 'funeralHomeInfo',
      },
      {
        path: 'memberOrderList',
      },
    ],
  })}`;
};

export const useFeventListhook = () => {
  const { bugoRole, isBugoAgencyWorker } = useAuth();
  const urlFunc = (page: number) => {
    return feventListHookUrl(page, 100, bugoRole());
  };

  useRecoilQueryInterVal(
    feventListStatusAtom,
    feventListDataAtom,
    urlFunc,
    fetcher,
    2000,
    isBugoAgencyWorker(),
  );
};

//* bugoManagPage에서 사용중
export const activeFeventListForWorkerHookUrl = (
  bugoRole: LowerRole,
  isAdmin: boolean,
) => {
  if (isAdmin) {
    return `${bugoRole}/fevent/?${queryString({
      options: {
        sort: {
          createdAt: -1,
        },
      },
      filter: {
        'meta.isDeleted': { $ne: true },
      },
      populate: isAdmin
        ? [
            {
              path: 'roomInfo',
            },
            {
              path: 'funeralHomeInfo',
            },
            {
              path: 'memberOrderList',
            },
            {
              path: 'user',
            },
            {
              path: 'bugoBrand',
            },
          ]
        : [
            {
              path: 'roomInfo',
            },
            {
              path: 'funeralHomeInfo',
            },
            {
              path: 'memberOrderList',
            },
          ],
    })}`;
  } else {
    return `${bugoRole}/fevent/?${queryString({
      options: {
        sort: {
          createdAt: -1,
        },
      },
      filter: {
        'meta.isDeleted': { $ne: true },
      },
      populate: isAdmin
        ? [
            {
              path: 'roomInfo',
            },
            {
              path: 'funeralHomeInfo',
            },
            {
              path: 'memberOrderList',
            },
            {
              path: 'user',
            },
            {
              path: 'bugoBrand',
            },
          ]
        : [
            {
              path: 'roomInfo',
            },
            {
              path: 'funeralHomeInfo',
            },
            {
              path: 'memberOrderList',
            },
          ],
    })}`;
  }
};

export const useActiveFeventListForWorkerHook = () => {
  const { bugoRole, isAdmin } = useAuth();

  useRecoilQuery(
    activeFeventListForWorkerStatusAtom,
    activeFeventListForWorkerDataAtom,
    activeFeventListForWorkerHookUrl(bugoRole(), isAdmin()),
    fetcher,
  );
};

export const feventDoingListHookUrl = (page: number, bugoRole: LowerRole) => {
  return `${bugoRole}/fevent/?${queryString({
    options: {
      sort: { createdAt: -1 },
      limit: 10,
      skip: 10 * page,
    },
    filter: { status: FeventStatus.Doing },
    populate: [
      { path: 'user' },
      {
        path: 'roomInfo',
      },
      {
        path: 'funeralHomeInfo',
      },
      {
        path: 'memberOrderList',
      },
    ],
  })}`;
};

export const useFeventDoingListhook = (page = 0) => {
  const { isAuthenticated, bugoRole } = useAuth();
  useRecoilQuery(
    feventDoingListStatusAtom,
    feventDoingListDataAtom,
    feventDoingListHookUrl(page, bugoRole()),
    fetcher,
    !isAuthenticated,
  );
};

//*상조관리자가 볼 fevent
//* export 엔드포인트 사용 → 페이지네이션 없이 전체 데이터 반환 (프론트에서 클라이언트 필터링/페이지네이션)
export const feventListAgencyAdminExportUrl = (
  bugoBrandId: string,
  dateRange?: {
    startDate: Date;
    endDate: Date;
  },
  dateType?: 'createdAt' | 'coffinOut',
) => {
  const params = new URLSearchParams();
  params.set('bugoBrandId', bugoBrandId);

  if (dateRange) {
    params.set('startDate', dateRange.startDate.toISOString());
    params.set('endDate', dateRange.endDate.toISOString());
  }
  if (dateType) {
    params.set('dateType', dateType);
  }

  return `/bugo-agency-admin/fevent/list/export?${params.toString()}`;
};

//* 페이지네이션 URL 생성 (추후 서버사이드 페이지네이션 전환용)
export const feventListAgencyAdminUrl = (
  bugoBrandId: string,
  params: {
    startDate?: string;
    endDate?: string;
    dateType?: 'createdAt' | 'coffinOut';
    page?: number;
    limit?: number;
  },
) => {
  const searchParams = new URLSearchParams();
  searchParams.set('bugoBrandId', bugoBrandId);
  if (params.startDate) searchParams.set('startDate', params.startDate);
  if (params.endDate) searchParams.set('endDate', params.endDate);
  if (params.dateType) searchParams.set('dateType', params.dateType);
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));

  return `/bugo-agency-admin/fevent/list?${searchParams.toString()}`;
};

//* Phase 1: export 엔드포인트로 전체 데이터 가져오기
//* - 백엔드에서 flat 변환 + summary 계산
//* - 프론트에서 검색/필터/페이지네이션 처리
export const useFeventListAgencyAdminHook = (
  bugoBrandId: string,
  dateRange?: {
    startDate: Date;
    endDate: Date;
  },
  dateType?: 'createdAt' | 'coffinOut',
) => {
  const { isAdmin, isBugoAgencyAdmin, isBugoAgencyBranchAdmin } = useAuth();

  useRecoilQuery(
    feventListAgencyAdminStatusAtomFamily(bugoBrandId),
    feventListAgencyAdminDataAtomFamily(bugoBrandId),
    feventListAgencyAdminExportUrl(bugoBrandId, dateRange, dateType),
    fetcher,
    !(isAdmin() || isBugoAgencyAdmin() || isBugoAgencyBranchAdmin()),
    false,
  );
};

export const feventCountByBugoBrandHookUrl = (
  bugoBrandId: string,
  bugoRole: LowerRole,
  dateFrom?: Date,
  dateTo?: Date,
) => {
  const dateFilterOn = dateFrom || dateTo;

  return `${bugoRole}/fevent/count/?${queryString({
    options: {
      sort: { createdAt: -1 },
    },
    filter: {
      bugoBrand: bugoBrandId,
      createdAt: dateFilterOn && {
        $gte: dateFrom,
        $lt: dateTo,
      },
    },
  })}`;
};

export const useFeventCountByBugoBrandhook = (
  bugoBrandId: string,
  atomId: string,
  dateFrom?: Date,
  dateTo?: Date,
) => {
  const { bugoRole, isAdmin, isBugoAgencyAdmin, isBugoAgencyBranchAdmin } = useAuth();
  useRecoilQuery(
    feventCountByBugoBrandStatusAtomFamily(atomId),
    feventCountByBugoBrandDataAtomFamily(atomId),
    feventCountByBugoBrandHookUrl(bugoBrandId, bugoRole(), dateFrom, dateTo),
    fetcher,
    !(isAdmin() || isBugoAgencyAdmin() || isBugoAgencyBranchAdmin()),
  );
};

export const feventByShopHookUrl = (shopId: string, sellerRole: LowerRole) => {
  return `${sellerRole}/fevent/?${queryString({
    options: {
      sort: { createdAt: -1 },
    },
    populate: [
      {
        path: 'funeralHomeInfo',
      },
      {
        path: 'roomInfo',
      },

      {
        path: 'memberOrderList',
        populate: {
          path: 'bugo',
        },
      },
    ],
    filter: {
      status: { $in: [FeventStatus.Doing, FeventStatus.Todo] },
    },
  })}`;
};

export const useFeventByShopHook = (shopId: string) => {
  const { sellerRole } = useAuth();
  useRecoilQuery(
    feventByShopStatusAtomFamily(shopId),
    feventByShopDataAtomFamily(shopId),
    feventByShopHookUrl(shopId, sellerRole()),
    fetcher,
  );
};
